import type { ChangeEvent } from "react";

interface NumberStepperProps {
  value: string;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

/**
 * Stepper input with `-` and `+` buttons flanking a bare number input.
 * Empty string is treated as "no limit" (unlimited).
 */
export function NumberStepper({
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  placeholder = "unlimited",
  ariaLabel,
  disabled = false,
}: NumberStepperProps) {
  const parseOrDefault = (def: number): number => {
    const n = Number(value);
    if (!value || !Number.isFinite(n)) return def;
    return Math.floor(n);
  };

  const clamp = (n: number): number => {
    let x = n;
    if (typeof min === "number") x = Math.max(min, x);
    if (typeof max === "number") x = Math.min(max, x);
    return x;
  };

  const handleDec = () => {
    if (disabled) return;
    const current = parseOrDefault(typeof max === "number" ? max : min + step);
    const next = clamp(current - step);
    onChange(String(next));
  };

  const handleInc = () => {
    if (disabled) return;
    const current = parseOrDefault(min - step);
    const next = clamp(current + step);
    onChange(String(next));
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === "") {
      onChange("");
      return;
    }
    const n = Number(v);
    if (!Number.isFinite(n)) return;
    onChange(String(Math.floor(clamp(n))));
  };

  return (
    <div
      className={`num-stepper${disabled ? " disabled" : ""}`}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        className="step-btn"
        onClick={handleDec}
        aria-label="Decrease"
        disabled={disabled}
        tabIndex={-1}
      >
        −
      </button>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={handleInput}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <button
        type="button"
        className="step-btn"
        onClick={handleInc}
        aria-label="Increase"
        disabled={disabled}
        tabIndex={-1}
      >
        +
      </button>
    </div>
  );
}
